import dayjs from "dayjs";
import "dayjs/locale/de";
import "dayjs/locale/en";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Grid,
  Typography,
  useTheme,
} from "@mui/material";
import { Check, ClipboardList } from "lucide-react";

import { useAuth } from "../../../provider";
import { useTranslation } from "../../../language";
import PageProtectedLayout from "../../../components/Page/PageProtectedLayout";
import LabWorkflowSteps from "../../../components/Steps/LabWorkflowSteps";
import ButtonsMain from "../../../components/Button/ButtonMain";
import CustomDialog from "../../../components/Dialog/DiaCompMedium";
import * as c from "../../../constants";

import SpeakQuest from "./Questionnaires/SpeakQuest";
import RecommendationsCheckIn from "./RecommendationsCheckIn";
import ActionHeader from "./ActionHeader";

// Visit states considered inactive — excluded from fallback resolution
const INACTIVE_STATES = [1, 2, 4, 5, 6, 41, 42];

export default function StateQuest() {
  const theme = useTheme();
  const { state } = useAuth();
  const { t, language } = useTranslation();
  dayjs.locale(language);

  const location = useLocation();
  const labVisitIdFromNav = location.state?.labVisitId;

  // ── Visit state ────────────────────────────────────────────────────────────
  const [visits, setVisits] = useState([]);
  const [currentVisit, setCurrentVisit] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // ── Resolved IDs via useMemo ───────────────────────────────────────────────
  const activeVisitId = useMemo(
    () => labVisitIdFromNav ?? currentVisit?.id ?? null,
    [labVisitIdFromNav, currentVisit]
  );

  const labVisit = useMemo(() => {
    if (labVisitIdFromNav) {
      return visits.find((v) => String(v.id) === String(labVisitIdFromNav)) ?? currentVisit;
    }
    return currentVisit;
  }, [labVisitIdFromNav, visits, currentVisit]);

  // ── 1. Fetch visits ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!state?.user?.id) return;
    let mounted = true;

    const fetchVisits = async () => {
      setPageLoading(true);
      try {
        if (labVisitIdFromNav) {
          // Fast path — we already know which visit we need
          const { data } = await axios.get(
            `${c.API_URL}/api/user-lab-visits/${labVisitIdFromNav}/`
          );
          if (mounted) {
            setVisits(data ? [data] : []);
            setCurrentVisit(data ?? null);
          }
        } else {
          // Fallback — fetch all, find the active one
          const { data } = await axios.get(`${c.API_URL}/api/user-lab-visits/`);
          const visitsArray = Array.isArray(data) ? data : data ? [data] : [];
          const active =
            visitsArray.find((v) => !INACTIVE_STATES.includes(v.state)) ?? null;
          if (mounted) {
            setVisits(visitsArray);
            setCurrentVisit(active);
          }
        }
      } catch (err) {
        console.error(err);
        if (mounted) setError("Unable to load lab visit.");
      } finally {
        if (mounted) setPageLoading(false);
      }
    };

    fetchVisits();
    return () => { mounted = false; };
  }, [state?.user?.id, labVisitIdFromNav]);

  // ── Guards ─────────────────────────────────────────────────────────────────
  if (pageLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (!labVisit || !activeVisitId) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <Typography variant="subtitle1" color="text.secondary">
          No active lab visit found. Please start a new lab visit.
        </Typography>
      </Box>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  // Gut-microbiome visits never reach this page — the webhook routes them to
  // state 41 (MicrobiomePath) instead of 3, so this only ever handles the
  // standard questionnaire.
  return (
    <PageProtectedLayout headline={t("lab_header5")} specify={t("lab_header6")}>
      <Grid
        container
        spacing={1}
        sx={{
          maxWidth: 1000,
          mb: 16,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {/* ── Workflow Stepper ── */}
        <Grid
          size={12}
          sx={{
            maxWidth: 800,
            my: 2,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <LabWorkflowSteps stepNumber={2} />
        </Grid>

        {/* ── Review intro ── */}
        <Grid
          size={12}
          sx={{
            maxWidth: 700,
            my: 6,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 3,
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h4" fontWeight={400}>
              {t("lab_quest_review_title1")}
            </Typography>
            <Typography variant="h4" fontWeight={700}>
              {t("lab_quest_review_title2")}
            </Typography>
          </Box>

          <Typography variant="body1" color="text.secondary" textAlign="center">
            {t("lab_quest_review_desc")}
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              width: "100%",
              border: "1px solid",
              borderRadius: 4,
              p: 2.5,
              ...(theme.palette.mode === "light"
                ? { bgcolor: "grey.C50", borderColor: "grey.C900" }
                : {
                    bgcolor: "greydark.C980",
                    borderColor: "greydark.C400",
                    backdropFilter: "blur(20px)",
                    WebkitBackdropFilter: "blur(20px)",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
                  }),
            }}
          >
            <ActionHeader
              number={1}
              label={t("lab_action1_header")}
              accentColor={theme.palette.tertiary.C300}
            />

            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
              <Box
                sx={{
                  flexShrink: 0,
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  bgcolor: submitted
                    ? `${theme.palette.traffic.green}22`
                    : `${theme.palette.tertiary.C300}22`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mt: "2px",
                }}
              >
                {submitted ? (
                  <Check size={16} strokeWidth={3} color={theme.palette.traffic.green} />
                ) : (
                  <ClipboardList size={16} color={theme.palette.tertiary.C300} />
                )}
              </Box>
              <Box>
                {submitted ? (
                  <Typography variant="body1" fontWeight={700}>
                    {t("lab_quest_review_info_title")}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {t("lab_quest_review_info_desc")}
                  </Typography>
                )}

                {!submitted && (
                  <ButtonsMain
                    action={() => setDialogOpen(true)}
                    name={t("lab_quest_review_btn")}
                    size="small"
                    sx={{ minWidth: 0, mt: 0, px: 1.5, py: 0.25, fontSize: "0.75rem" }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Grid>

        {/* ── Recommendation check-in ── */}
        <Grid
          size={12}
          sx={{
            maxWidth: 700,
            mt: 4,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <RecommendationsCheckIn />
        </Grid>
      </Grid>

      <CustomDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={t("onb_quest_form_title")}
        content={
          <SpeakQuest
            labVisitId={activeVisitId}
            onSubmitSuccess={() => {
              setDialogOpen(false);
              setSubmitted(true);
            }}
          />
        }
      />
    </PageProtectedLayout>
  );
}
